import * as angular from 'angular';
import {IPromise} from 'angular';
import {ServiceType} from '../../deliveries/types/service.type';
import {
  AccountAgentCategoryIds,
  applyAccountAgentCategoryChange,
  categorizePreferredAgents,
  sanitizeAccountAgentCategoryIds
} from '../utils/account-preferred-agents.util';
import {AccountWithPreferredAgents} from '../models/account-with-preferred-agents.model';
import {BatchRequestsService} from '../../batch/services/batch-requests.service';
import {AgentType} from '../../agents/types/agent.type';

export class AgentAccountAssignmentDialogService {

  /* @ngInject */
  constructor(private $mdDialog, private $log, private batchRequestsService: BatchRequestsService) {
  }

  showEditAssignmentsDialog(
    event: Event,
    accountWithAgents: AccountWithPreferredAgents,
    serviceType: string
  ): IPromise<{serviceType: string, categoryIds: AccountAgentCategoryIds}> {
    const self = this;
    const preferredAgents = accountWithAgents.preferredAgents || [];
    const initialCategoryIds = sanitizeAccountAgentCategoryIds(
      categorizePreferredAgents(preferredAgents, serviceType)
    );

    return this.$mdDialog.show({
      templateUrl: 'app/modules/account/views/dialogs/edit-account-agent-assignments-dialog.html',
      parent: angular.element(document.querySelector('#popupContainer')),
      targetEvent: event,
      clickOutsideToClose: true,
      fullscreen: false,
      controller: function ($scope) {
        $scope.accountName = accountWithAgents.accountReference?.name || '';
        $scope.managementTitle = serviceType === ServiceType.Operation
          ? 'Field Service Agent Management'
          : 'Delivery Agent Management';
        $scope.serviceType = serviceType;
        // CON-14083 - a Delivery assignment may only pick Drivers, a Field Service
        // assignment may only pick Service Agents.
        $scope.agentTypes = serviceType === ServiceType.Operation
          ? [AgentType.ServiceAgent]
          : [AgentType.Driver];
        $scope.primaryIds = angular.copy(initialCategoryIds.primaryIds);
        $scope.secondaryIds = angular.copy(initialCategoryIds.secondaryIds);
        $scope.dedicatedIds = angular.copy(initialCategoryIds.dedicatedIds);
        $scope.restrictedIds = angular.copy(initialCategoryIds.restrictedIds);
        $scope.isSubmitting = false;

        const applyCategoryChange = (category, ids) => {
          const categoryIds = applyAccountAgentCategoryChange({
            primaryIds: $scope.primaryIds,
            secondaryIds: $scope.secondaryIds,
            dedicatedIds: $scope.dedicatedIds,
            restrictedIds: $scope.restrictedIds
          }, category, ids || []);
          $scope.primaryIds = categoryIds.primaryIds;
          $scope.secondaryIds = categoryIds.secondaryIds;
          $scope.dedicatedIds = categoryIds.dedicatedIds;
          $scope.restrictedIds = categoryIds.restrictedIds;
        };

        $scope.onPrimaryChange = (ids) => {
          applyCategoryChange('primary', ids);
        };

        $scope.onSecondaryChange = (ids) => {
          applyCategoryChange('secondary', ids);
        };

        $scope.onDedicatedChange = (ids) => {
          applyCategoryChange('dedicated', ids);
        };

        $scope.onRestrictedChange = (ids) => {
          applyCategoryChange('restricted', ids);
        };

        $scope.cancel = self.$mdDialog.cancel;

        $scope.submit = () => {
          const categoryIds = sanitizeAccountAgentCategoryIds({
            primaryIds: $scope.primaryIds || [],
            secondaryIds: $scope.secondaryIds || [],
            dedicatedIds: $scope.dedicatedIds || [],
            restrictedIds: $scope.restrictedIds || []
          });

          self.$mdDialog.hide({
            serviceType: $scope.serviceType,
            categoryIds
          });
        };
      }
    });
  }

  /**
   * CON-13947 - "Upload <type> Excel" modal. Resolves with the validated rows
   * (ImportVerification[]) returned by /{importerPath}/validate so the caller can review
   * them before committing the import.
   */
  showUploadAgentAssignmentsDialog(event: Event, importType, schemas?: any[]): IPromise<any[]> {
    const self = this;
    return this.$mdDialog.show({
      templateUrl: 'app/modules/account/views/dialogs/upload-agent-assignments-dialog.html',
      parent: angular.element(document.body),
      targetEvent: event,
      clickOutsideToClose: false,
      fullscreen: true,
      controller: function ($scope) {
        $scope.loading = false;
        $scope.file = undefined;
        $scope.schemaId = undefined;
        $scope.validationError = undefined;
        $scope.entityLabel = importType.label;
        $scope.schemaOptionsWrapper = {
          list: (schemas || []).map((schema) => {
            return {id: schema.id, name: schema.schemaName};
          }),
          propertyRef: 'name'
        };

        $scope.cancel = self.$mdDialog.cancel;

        $scope.onSelectSchema = (schemaId) => {
          $scope.schemaId = schemaId;
          $scope.validationError = undefined;
        };

        $scope.onAddFile = (file) => {
          $scope.file = file;
          $scope.validationError = undefined;
        };

        $scope.onUpload = () => {
          if (!$scope.file) {
            $scope.validationError = 'Please select a file to upload';
            return;
          }

          $scope.loading = true;

          self.batchRequestsService.validateAgentAssignments(importType.importerPath, $scope.file, $scope.schemaId)
            .then((rows: any) => {
              self.$mdDialog.hide(rows || []);
            })
            .catch((error) => {
              $scope.validationError = 'Failed to validate the uploaded file';
              self.$log.error('Failed to validate agent assignments excel, ' +
                'agentAccountAssignmentDialogService@showUploadAgentAssignmentsDialog', error);
            })
            .finally(() => {
              $scope.loading = false;
            });
        };
      }
    });
  }
}

angular
  .module('bringoz.admin.accounts')
  .service('agentAccountAssignmentDialogService', AgentAccountAssignmentDialogService);
